import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { todayLocalISO } from "@/lib/dates";
import { getUser } from "@/lib/auth";
import { initSync, queueSize } from "@/lib/offline";
import { AppHeader } from "@/components/AppHeader";
import { PhotoUpload } from "@/components/PhotoUpload";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Camera, MapPin, IndianRupee, GlassWater, Minus, Plus, CheckCircle2,
  AlertTriangle, TrendingUp, Clock, Target, Package, Receipt, Wrench, Wifi, WifiOff, Banknote, MessagesSquare,
} from "lucide-react";
import { Link } from "react-router-dom";
import { formatINR, formatNum } from "@/lib/format";

const TARGET = 400;

const Stepper = ({ label, value, onChange, step = 1, min = 0, max = 99999, testId }) => (
  <div>
    <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">{label}</label>
    <div className="mt-2 flex items-center gap-2">
      <Button type="button" data-testid={`${testId}-minus`} onClick={() => onChange(Math.max(min, value - step))}
        className="h-14 w-14 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-900 tap-btn shrink-0">
        <Minus className="h-5 w-5" />
      </Button>
      <Input data-testid={testId} inputMode="decimal"
        className="h-14 text-2xl text-center font-display font-bold flex-1"
        value={value}
        onChange={(e) => {
          const v = e.target.value.replace(/[^\d.]/g, "");
          onChange(Math.max(min, Math.min(max, Number(v) || 0)));
        }} />
      <Button type="button" data-testid={`${testId}-plus`} onClick={() => onChange(Math.min(max, value + step))}
        className="h-14 w-14 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-900 tap-btn shrink-0">
        <Plus className="h-5 w-5" />
      </Button>
    </div>
  </div>
);

export default function PromoterHome() {
  const user = getUser();
  const [store, setStore] = useState(null);
  const [attendance, setAttendance] = useState(null);
  const [sales, setSales] = useState(null);
  const [loading, setLoading] = useState(true);
  const [online, setOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(queueSize());

  const [attnOpen, setAttnOpen] = useState(false);
  const [salesOpen, setSalesOpen] = useState(false);
  const [stockOpen, setStockOpen] = useState(false);
  const [expOpen, setExpOpen] = useState(false);
  const [machOpen, setMachOpen] = useState(false);

  // Back-date support for sales + attendance
  const todayISO = () => todayLocalISO();
  const [salesDate, setSalesDate] = useState(todayISO());
  const [salesLateReason, setSalesLateReason] = useState("");
  const [attnDate, setAttnDate] = useState(todayISO());
  const [attnLateReason, setAttnLateReason] = useState("");
  const isSalesBackdated = salesDate !== todayISO();
  const isAttnBackdated = attnDate !== todayISO();
  const [cashOpen, setCashOpen] = useState(false);

  // Attendance selfie
  const [selfie, setSelfie] = useState(null);

  // Sales form
  const [glassesSold, setGlassesSold] = useState(0);
  const [damaged, setDamaged] = useState(0);
  const [cash, setCash] = useState(0);
  const [upi, setUpi] = useState(0);
  const [card, setCard] = useState(0);
  const [remarks, setRemarks] = useState("");

  // Stock — Item 1 (2026-02-29): Cups is the ONLY item promoters count.
  // ITEM A.1 (2026-02-29): Promoter enters CLOSING cups only. Opening is server-derived
  // from yesterday's closing and hidden from the Promoter role.
  const [closingStock, setClosingStock] = useState(0);
  const [stockPhoto, setStockPhoto] = useState(null);
  const [lastSwaps, setLastSwaps] = useState({ BIB: null, CO2: null });
  const [swapBusy, setSwapBusy] = useState(false);
  // ITEM A.3 (2026-02-29): mini-form (Yes/No) instead of one-tap for BIB/CO2 changed
  const [swapDialog, setSwapDialog] = useState({ open: false, item: null, changed: null });

  // Expense — ITEM A.4 (2026-02-29): only 3 categories on the Promoter form.
  const [expCat, setExpCat] = useState("Water Jar");
  const [expPay, setExpPay] = useState("");   // Mode of Payment — required. Cash | Card | UPI
  const [expAmt, setExpAmt] = useState(0);
  const [expPhoto, setExpPhoto] = useState(null);
  const [expNotes, setExpNotes] = useState("");

  // Machine issue — ITEM A.2 (2026-02-29): single Down checkbox
  const [machDown, setMachDown] = useState(false);
  const [machPhoto, setMachPhoto] = useState(null);
  const [machNotes, setMachNotes] = useState("");

  // Cash deposit
  const [cashCollected, setCashCollected] = useState(0);
  const [cashDeposited, setCashDeposited] = useState(0);
  const [depPhoto, setDepPhoto] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [stores, att, sls, swaps] = await Promise.all([
        api.get("/stores"),
        api.get("/attendance/today"),
        api.get("/sales/today"),
        api.get(`/stock-swaps/latest?store_id=${user.assigned_store}`).catch(() => ({ data: {} })),
      ]);
      setStore(stores.data.find((x) => x.id === user.assigned_store));
      setAttendance(att.data);
      setSales(sls.data);
      if (sls.data) {
        setGlassesSold(sls.data.glasses_sold); setDamaged(sls.data.damaged_glasses);
        setCash(sls.data.cash_amount); setUpi(sls.data.upi_amount);
        setCard(sls.data.card_amount || 0);
        setCashCollected(sls.data.cash_amount);
      }
      setLastSwaps({
        BIB: swaps.data?.BIB?.last || null,
        CO2: swaps.data?.CO2?.last || null,
      });
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, [user.assigned_store]);

  useEffect(() => {
    load();
    initSync();
    const onOn = () => { setOnline(true); setPending(queueSize()); };
    const onOff = () => setOnline(false);
    window.addEventListener("online", onOn); window.addEventListener("offline", onOff);
    const t = setInterval(() => setPending(queueSize()), 3000);
    return () => {
      window.removeEventListener("online", onOn); window.removeEventListener("offline", onOff);
      clearInterval(t);
    };
  }, [load]);

  // Strict GPS capture — the promoter MUST allow location for sign-in.
  // No random-jitter fallback anywhere; no bypass. Returns { lat, lng } or throws.
  const getGpsStrict = () => new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("Location is not supported on this device"));
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }),
      (err) => {
        const msg = err.code === 1
          ? "Location permission denied. Enable location access to mark attendance."
          : "Could not get your location. Move to a spot with good GPS signal and try again.";
        reject(new Error(msg));
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  });

  const GEOFENCE_M = 200;

  // Haversine distance in metres between two lat/lng points.
  const haversineM = (lat1, lng1, lat2, lng2) => {
    const R = 6371000;
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a = Math.sin(dLat / 2) ** 2 +
              Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(a));
  };

  const markAttendance = async () => {
    if (!store) return;
    if (isAttnBackdated && !attnLateReason.trim()) return toast.error("Please add a reason for this back-dated attendance");
    let gps;
    toast.loading("Checking your location…", { id: "geo" });
    try {
      gps = await getGpsStrict();
    } catch (e) {
      toast.dismiss("geo");
      return toast.error(e.message || "Location required");
    }
    toast.dismiss("geo");
    try {
      const payload = {
        store_id: store.id, gps_lat: gps.lat, gps_lng: gps.lng, selfie_photo: selfie,
      };
      if (isAttnBackdated) {
        payload.date = attnDate;
        payload.late_reason = attnLateReason.trim();
      }
      const { data } = await api.post("/attendance", payload);
      setAttendance(data); setAttnOpen(false); setSelfie(null);
      setAttnDate(todayISO()); setAttnLateReason("");
      toast.success(isAttnBackdated
        ? "Back-dated attendance submitted — pending Co-ordinator approval"
        : `Signed in · ${data.status}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const signOut = async () => {
    if (!attendance || attendance.sign_out_time) return;
    if (!window.confirm("Sign out for today? Your store's LIVE indicator will be removed immediately.")) return;
    try {
      const { data } = await api.post("/attendance/signout", { kind: "manual" });
      setAttendance(data);
      toast.success("Signed out. Have a good evening!");
    } catch (e) { toast.error(e.response?.data?.detail || "Sign out failed"); }
  };

  const submitSales = async () => {
    const total = glassesSold * 9;
    if (Math.abs((cash + upi + card) - total) > 0.5) {
      if (!window.confirm(`Cash+UPI+Card ₹${cash+upi+card} ≠ ₹${total}. Submit anyway (flagged)?`)) return;
    }
    if (isSalesBackdated && !salesLateReason.trim()) {
      toast.error("Please add a reason for this back-dated entry");
      return;
    }
    try {
      const payload = {
        store_id: store.id, opening_glass_stock: 0, glasses_sold: glassesSold,
        damaged_glasses: damaged, cash_amount: cash, upi_amount: upi, card_amount: card, remarks,
      };
      if (isSalesBackdated) {
        payload.date = salesDate;
        payload.late_reason = salesLateReason.trim();
      }
      const { data } = await api.post("/sales", payload);
      setSales(data); setSalesOpen(false);
      setSalesDate(todayISO()); setSalesLateReason("");
      toast.success(isSalesBackdated
        ? "Back-dated sale submitted for approval (LATE ENTRY)"
        : "Sales submitted");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const submitStock = async () => {
    try {
      // ITEM A.1 — Cups: promoter posts CLOSING only. Backend auto-derives opening.
      await api.post("/consumables", {
        store_id: store.id, item: "Cups",
        closing_stock: closingStock, photo_path: stockPhoto,
      });
      setStockOpen(false); setStockPhoto(null); setClosingStock(0);
      toast.success("Cups closing stock saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openSwapDialog = (item) => setSwapDialog({ open: true, item, changed: null });
  const submitSwap = async () => {
    if (swapBusy || !swapDialog.item || swapDialog.changed === null) return;
    setSwapBusy(true);
    try {
      const { data } = await api.post("/stock-swaps", {
        store_id: store.id, item: swapDialog.item, changed: swapDialog.changed,
      });
      if (data.logged) {
        setLastSwaps((p) => ({ ...p, [swapDialog.item]: data }));
        toast.success(`${swapDialog.item} change logged`);
      } else {
        toast.success(`Recorded: no ${swapDialog.item} change today`);
      }
      setSwapDialog({ open: false, item: null, changed: null });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSwapBusy(false); }
  };

  const swapLabel = (item) => {
    const s = lastSwaps[item];
    if (!s?.at) return "No change logged yet";
    try {
      const then = new Date(s.at);
      const diff = Math.max(0, (Date.now() - then.getTime()) / 60000);
      if (diff < 1) return "Just now";
      if (diff < 60) return `${Math.round(diff)} min ago`;
      const h = diff / 60;
      if (h < 24) return `${h.toFixed(1)}h ago`;
      return `${Math.floor(h / 24)}d ago`;
    } catch { return "—"; }
  };

  const submitExpense = async () => {
    if (expAmt <= 0) return toast.error("Enter amount");
    if (!expPay) return toast.error("Select Mode of Payment");
    try {
      await api.post("/expenses", {
        store_id: store.id, category: expCat, amount: expAmt,
        payment_mode: expPay,
        receipt_photo: expPhoto, billable_to_client: expCat !== "Miscellaneous", notes: expNotes,
      });
      setExpOpen(false); setExpAmt(0); setExpPhoto(null); setExpNotes(""); setExpPay("");
      toast.success("Expense logged");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const submitMachine = async () => {
    try {
      // ITEM A.2 — single Down toggle. Backend auto-raises Issue if down=true.
      await api.post("/machine-logs", {
        store_id: store.id, down: machDown, photo: machPhoto, notes: machNotes,
      });
      setMachOpen(false); setMachDown(false); setMachPhoto(null); setMachNotes("");
      toast.success(machDown ? "Machine marked Down · issue raised" : "Machine status recorded");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const submitDeposit = async () => {
    try {
      await api.post("/cash-deposits", {
        store_id: store.id, cash_collected: cashCollected, cash_deposited: cashDeposited,
        deposit_slip_photo: depPhoto,
      });
      setCashOpen(false); setDepPhoto(null);
      toast.success("Cash deposit recorded");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Loading…</div>;
  if (!store) return (
    <div className="min-h-screen"><AppHeader title="Promoter" />
      <div className="p-8 text-center text-gray-500">No store assigned. Please contact your supervisor.</div>
    </div>);

  const targetPct = Math.min(100, ((sales?.glasses_sold || 0) / TARGET) * 100);
  const totalSales = (sales?.glasses_sold || 0) * 9;

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <AppHeader title={store.store_name} subtitle={`${store.store_code} · ${store.city}`}
        right={
          <div className="flex items-center gap-1 text-xs">
            {online ? <Wifi className="h-4 w-4 text-green-400" /> : <WifiOff className="h-4 w-4 text-amber-400" />}
            {pending > 0 && <Badge className="bg-amber-500 hover:bg-amber-500">{pending} queued</Badge>}
          </div>
        } />

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-4 stagger-in">
        {/* Today progress */}
        <Card className="p-6 rounded-2xl border-gray-200 shadow-sm bg-white">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-gray-500">Today's target</div>
              <div className="font-display text-4xl font-black text-gray-900 mt-1">
                {formatNum(sales?.glasses_sold || 0)}<span className="text-lg text-gray-400 font-semibold"> / {TARGET} cups</span>
              </div>
              <div className="mt-1 text-sm text-gray-600">
                {formatINR(totalSales)} · Cash {formatINR(sales?.cash_amount || 0)} · UPI {formatINR(sales?.upi_amount || 0)} · Card {formatINR(sales?.card_amount || 0)}
              </div>
            </div>
            <div className={`h-14 w-14 rounded-xl flex items-center justify-center ${targetPct >= 100 ? "bg-green-100 text-green-700" : "bg-red-50 text-brand-red"}`}>
              <Target className="h-6 w-6" />
            </div>
          </div>
          <Progress value={targetPct} className="mt-4 h-3" data-testid="target-progress" />
          <div className="mt-2 text-xs text-gray-500">{targetPct.toFixed(0)}% of daily target</div>
        </Card>

        {/* Status tiles */}
        <div className="grid grid-cols-2 gap-3">
          <Card className={`p-4 rounded-xl border ${attendance ? (attendance.sign_out_time ? "border-gray-200 bg-gray-50" : "border-green-200 bg-green-50") : "border-gray-200 bg-white"}`} data-testid="attendance-status-card">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.1em] text-gray-600">
              <Clock className="h-3.5 w-3.5" /> Attendance
            </div>
            <div className="mt-1 font-display text-lg font-bold text-gray-900">
              {!attendance ? "Not signed in" :
                attendance.sign_out_time ? "Signed out" : `Signed in · ${attendance.status}`}
            </div>
            {attendance && (
              <div className="text-[11px] text-gray-500">
                In · {new Date(attendance.sign_in_time || attendance.check_in_time).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                {attendance.sign_out_time && <> · Out · {new Date(attendance.sign_out_time).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</>}
              </div>
            )}
          </Card>
          <Card className={`p-4 rounded-xl border ${sales ? "border-green-200 bg-green-50" : "border-gray-200 bg-white"}`} data-testid="sales-status-card">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.1em] text-gray-600">
              <TrendingUp className="h-3.5 w-3.5" /> Sales entry
            </div>
            <div className="mt-1 font-display text-lg font-bold text-gray-900">{sales ? "Submitted" : "Pending"}</div>
            {sales?.payment_check_flag && <Badge variant="destructive" className="mt-1 text-[10px]">Payment mismatch</Badge>}
          </Card>
        </div>

        {/* Big action buttons */}
        <div className="space-y-3">
          {!attendance && (
            <Button data-testid="mark-attendance-btn" onClick={() => setAttnOpen(true)}
              className="w-full min-h-[72px] rounded-2xl bg-brand-red hover:bg-[#B31721] text-white font-semibold text-lg tap-btn justify-start px-6 shadow-sm">
              <Camera className="h-6 w-6 mr-3" />
              <div className="text-left">
                <div>Sign In</div>
                <div className="text-xs font-normal text-white/80">Selfie + location check (within 200m of store)</div>
              </div>
            </Button>
          )}
          {attendance && !attendance.sign_out_time && (
            <Button data-testid="sign-out-btn" onClick={signOut}
              className="w-full min-h-[72px] rounded-2xl bg-neutral-900 hover:bg-neutral-800 text-white font-semibold text-lg tap-btn justify-start px-6 shadow-sm">
              <Clock className="h-6 w-6 mr-3" />
              <div className="text-left">
                <div>Sign Out</div>
                <div className="text-xs font-normal text-white/80">End your shift — store LIVE indicator turns off immediately</div>
              </div>
            </Button>
          )}
          {attendance && attendance.sign_out_time && (
            <Card className="p-4 rounded-2xl border-gray-200 bg-gray-50 text-center text-sm text-gray-600" data-testid="signed-out-card">
              You have signed out for the day
              {attendance.sign_out_kind === "auto_midnight" && (
                <div className="text-[11px] text-amber-700 mt-1">(auto sign-out · day end)</div>
              )}
            </Card>
          )}
          <Button data-testid="enter-sales-btn" onClick={() => setSalesOpen(true)}
            className="w-full min-h-[72px] rounded-2xl bg-[#0A0A0A] hover:bg-neutral-800 text-white font-semibold text-lg tap-btn justify-start px-6 shadow-sm">
            <GlassWater className="h-6 w-6 mr-3" />
            <div className="text-left">
              <div>{sales ? "Update Today's Sales" : "Enter Today's Sales"}</div>
              <div className="text-xs font-normal text-white/70">Cups, cash, UPI</div>
            </div>
          </Button>

          <div className="grid grid-cols-2 gap-3">
            <Button data-testid="update-stock-btn" onClick={() => setStockOpen(true)}
              className="min-h-[80px] rounded-2xl bg-white hover:bg-gray-50 text-gray-900 border border-gray-200 tap-btn justify-start px-4 shadow-sm">
              <Package className="h-5 w-5 mr-2 text-brand-red" />
              <div className="text-left">
                <div className="font-semibold">Cups Stock</div>
                <div className="text-[11px] text-gray-500 font-normal">BIB · CO2 auto-derived</div>
              </div>
            </Button>
            <Button data-testid="log-expense-btn" onClick={() => setExpOpen(true)}
              className="min-h-[80px] rounded-2xl bg-white hover:bg-gray-50 text-gray-900 border border-gray-200 tap-btn justify-start px-4 shadow-sm">
              <Receipt className="h-5 w-5 mr-2 text-brand-red" />
              <div className="text-left">
                <div className="font-semibold">Log Expense</div>
                <div className="text-[11px] text-gray-500 font-normal">Water Jar · Machine Repair · Misc</div>
              </div>
            </Button>
            <Button data-testid="machine-issue-btn" onClick={() => setMachOpen(true)}
              className="min-h-[80px] rounded-2xl bg-white hover:bg-gray-50 text-gray-900 border border-gray-200 tap-btn justify-start px-4 shadow-sm">
              <Wrench className="h-5 w-5 mr-2 text-brand-red" />
              <div className="text-left">
                <div className="font-semibold">Machine Issue</div>
                <div className="text-[11px] text-gray-500 font-normal">Report downtime</div>
              </div>
            </Button>
            <Button data-testid="cash-deposit-btn" onClick={() => setCashOpen(true)}
              className="min-h-[80px] rounded-2xl bg-white hover:bg-gray-50 text-gray-900 border border-gray-200 tap-btn justify-start px-4 shadow-sm">
              <Banknote className="h-5 w-5 mr-2 text-brand-red" />
              <div className="text-left">
                <div className="font-semibold">Cash Deposit</div>
                <div className="text-[11px] text-gray-500 font-normal">Bank / slip photo</div>
              </div>
            </Button>
            {/* One-tap physical swap buttons (Item 1) */}
            <Button data-testid="bib-changed-btn" onClick={() => openSwapDialog("BIB")} disabled={swapBusy}
              className="min-h-[80px] rounded-2xl bg-white hover:bg-gray-50 text-gray-900 border-2 border-brand-red/60 tap-btn justify-start px-4 shadow-sm">
              <Package className="h-5 w-5 mr-2 text-brand-red" />
              <div className="text-left">
                <div className="font-semibold">BIB changed</div>
                <div className="text-[11px] text-gray-500 font-normal" data-testid="bib-last-swap">
                  Last: {swapLabel("BIB")}
                </div>
              </div>
            </Button>
            <Button data-testid="co2-changed-btn" onClick={() => openSwapDialog("CO2")} disabled={swapBusy}
              className="min-h-[80px] rounded-2xl bg-white hover:bg-gray-50 text-gray-900 border-2 border-brand-red/60 tap-btn justify-start px-4 shadow-sm">
              <Package className="h-5 w-5 mr-2 text-brand-red" />
              <div className="text-left">
                <div className="font-semibold">CO2 changed</div>
                <div className="text-[11px] text-gray-500 font-normal" data-testid="co2-last-swap">
                  Last: {swapLabel("CO2")}
                </div>
              </div>
            </Button>
            <Link to="/chat" data-testid="nav-team-chat-tile"
              className="min-h-[80px] rounded-2xl bg-white hover:bg-gray-50 text-gray-900 border border-gray-200 tap-btn shadow-sm flex items-center justify-start px-4 sm:col-span-2">
              <MessagesSquare className="h-5 w-5 mr-2 text-brand-red" />
              <div className="text-left">
                <div className="font-semibold">Team Chat</div>
                <div className="text-[11px] text-gray-500 font-normal">Talk to your Co-ordinator, IT, Manager, Admin — one shared room</div>
              </div>
            </Link>
          </div>
        </div>
      </main>

      {/* Attendance dialog */}
      <Dialog open={attnOpen} onOpenChange={setAttnOpen}>
        <DialogContent data-testid="attendance-dialog" className="rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Sign In</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <PhotoUpload value={selfie} onChange={setSelfie} testId="selfie-upload" label="Selfie" />
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <MapPin className="h-4 w-4 text-brand-red" />
              GPS auto-captured
            </div>
            {/* Back-date attendance */}
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500 block mb-2">Date</label>
              <input
                type="date"
                data-testid="attendance-date-input"
                className="w-full h-12 rounded-lg border border-gray-200 px-3 text-sm"
                value={attnDate}
                max={todayISO()}
                onChange={(e) => setAttnDate(e.target.value || todayISO())}
              />
              {isAttnBackdated && (
                <div className="mt-2 space-y-2">
                  <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5">
                    LATE ENTRY — needs Co-ordinator approval before it counts in reports.
                  </div>
                  <Textarea
                    data-testid="attendance-late-reason"
                    placeholder="Reason for back-dating (required)"
                    rows={2}
                    value={attnLateReason}
                    onChange={(e) => setAttnLateReason(e.target.value)}
                  />
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAttnOpen(false)}>Cancel</Button>
            <Button data-testid="confirm-attendance-btn" onClick={markAttendance} className="bg-brand-red hover:bg-[#B31721]">
              <CheckCircle2 className="h-4 w-4 mr-2" /> {isAttnBackdated ? "Submit for approval" : "Confirm Sign-In"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sales dialog */}
      <Dialog open={salesOpen} onOpenChange={setSalesOpen}>
        <DialogContent data-testid="sales-dialog" className="rounded-2xl max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Today's Sales</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {/* Back-date sales */}
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500 block mb-2">Sale date</label>
              <input
                type="date"
                data-testid="sales-date-input"
                className="w-full h-12 rounded-lg border border-gray-200 px-3 text-sm"
                value={salesDate}
                max={todayISO()}
                onChange={(e) => setSalesDate(e.target.value || todayISO())}
              />
              {isSalesBackdated && (
                <div className="mt-2 space-y-2">
                  <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5">
                    LATE ENTRY — counts in totals only after Co-ordinator/Admin approval.
                  </div>
                  <Textarea
                    data-testid="sales-late-reason"
                    placeholder="Reason for back-dating (required)"
                    rows={2}
                    value={salesLateReason}
                    onChange={(e) => setSalesLateReason(e.target.value)}
                  />
                </div>
              )}
            </div>
            <Stepper testId="cups-sold-input" label="Cups sold" value={glassesSold} onChange={setGlassesSold} step={10} />
            <Stepper testId="damaged-input" label="Shrinkage" value={damaged} onChange={setDamaged} />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Cash ₹</label>
                <div className="relative">
                  <IndianRupee className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input data-testid="cash-input" inputMode="numeric" className="h-14 pl-9 text-lg font-semibold"
                    value={cash} onChange={(e) => setCash(Number(e.target.value.replace(/\D/g, "")) || 0)} />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">UPI ₹</label>
                <div className="relative">
                  <IndianRupee className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input data-testid="upi-input" inputMode="numeric" className="h-14 pl-9 text-lg font-semibold"
                    value={upi} onChange={(e) => setUpi(Number(e.target.value.replace(/\D/g, "")) || 0)} />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3">
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Card ₹</label>
                <div className="relative">
                  <IndianRupee className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input data-testid="card-input" inputMode="numeric" className="h-14 pl-9 text-lg font-semibold"
                    value={card} onChange={(e) => setCard(Number(e.target.value.replace(/\D/g, "")) || 0)} />
                </div>
              </div>
            </div>
            <div className="rounded-xl bg-gray-50 p-3 text-sm">
              <div className="flex justify-between"><span className="text-gray-600">Expected (cups × ₹9)</span><span className="font-semibold">{formatINR(glassesSold*9)}</span></div>
              <div className="flex justify-between mt-1"><span className="text-gray-600">Cash + UPI + Card</span><span className="font-semibold">{formatINR(cash+upi+card)}</span></div>
              {Math.abs((cash+upi+card) - glassesSold*9) > 0.5 && (
                <div className="mt-2 text-xs text-red-600 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Payment mismatch — will be flagged.</div>
              )}
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Remarks (optional)</label>
              <Textarea data-testid="remarks-input" value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSalesOpen(false)}>Cancel</Button>
            <Button data-testid="submit-sales-btn" onClick={submitSales} className="bg-brand-red hover:bg-[#B31721]">Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stock dialog — Closing Cups only (Opening auto-derived by backend) — Item A.1 */}
      <Dialog open={stockOpen} onOpenChange={setStockOpen}>
        <DialogContent data-testid="stock-dialog" className="rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Closing Cups</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
              Enter only the CLOSING cup count for today. BIB and CO2 are auto-derived from cups sold.
              Water Jar usage is captured via Log Expense.
            </div>
            <Stepper testId="closing-stock-input" label="Cups closing stock" value={closingStock} onChange={setClosingStock} step={10} />
            <PhotoUpload value={stockPhoto} onChange={setStockPhoto} testId="stock-photo" label="Stock photo (optional)" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStockOpen(false)}>Cancel</Button>
            <Button data-testid="submit-stock-btn" onClick={submitStock} className="bg-brand-red hover:bg-[#B31721]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Expense dialog */}
      <Dialog open={expOpen} onOpenChange={setExpOpen}>
        <DialogContent data-testid="expense-dialog" className="rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Log Expense</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Category</label>
              <Select value={expCat} onValueChange={setExpCat}>
                <SelectTrigger data-testid="expense-cat-select" className="h-14"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {/* ITEM A.4 (2026-02-29) — Promoter form limited to these 3 categories only */}
                  <SelectItem value="Water Jar">Water Jar</SelectItem>
                  <SelectItem value="Machine Repair">Machine Repair</SelectItem>
                  <SelectItem value="Miscellaneous">Miscellaneous</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Mode of Payment *</label>
              <Select value={expPay} onValueChange={setExpPay}>
                <SelectTrigger data-testid="expense-pay-select" className="h-14"><SelectValue placeholder="Select mode" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Cash">Cash</SelectItem>
                  <SelectItem value="Card">Card</SelectItem>
                  <SelectItem value="UPI">UPI</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Amount ₹</label>
              <div className="relative">
                <IndianRupee className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input data-testid="expense-amount-input" inputMode="numeric" className="h-14 pl-9 text-lg font-semibold"
                  value={expAmt} onChange={(e) => setExpAmt(Number(e.target.value.replace(/\D/g, "")) || 0)} />
              </div>
            </div>
            <PhotoUpload value={expPhoto} onChange={setExpPhoto} testId="expense-photo" label="Receipt photo" />
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Notes</label>
              <Textarea data-testid="expense-notes" value={expNotes} onChange={(e) => setExpNotes(e.target.value)} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExpOpen(false)}>Cancel</Button>
            <Button data-testid="submit-expense-btn" onClick={submitExpense} className="bg-brand-red hover:bg-[#B31721]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Machine dialog — ITEM A.2: single Down checkbox, auto-raises Issue when true */}
      <Dialog open={machOpen} onOpenChange={setMachOpen}>
        <DialogContent data-testid="machine-dialog" className="rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Machine Status</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <label className="flex items-center gap-3 rounded-xl bg-gray-50 border border-gray-200 p-4 cursor-pointer"
                    data-testid="machine-down-toggle-row">
              <input type="checkbox" data-testid="machine-down-checkbox"
                      className="h-5 w-5 accent-red-600"
                      checked={machDown} onChange={(e) => setMachDown(e.target.checked)} />
              <span className="font-semibold text-gray-900">Machine is Down right now</span>
            </label>
            <div className="text-xs text-gray-500">
              Leave unchecked if the machine is running normally. When ticked, an Issue will be
              auto-raised and Co-ordinator / Manager / Admin will be notified.
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Notes (optional)</label>
              <Textarea data-testid="machine-notes" value={machNotes} onChange={(e) => setMachNotes(e.target.value)} rows={2} />
            </div>
            <PhotoUpload value={machPhoto} onChange={setMachPhoto} testId="machine-photo" label="Photo (optional)" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMachOpen(false)}>Cancel</Button>
            <Button data-testid="submit-machine-btn" onClick={submitMachine} className="bg-brand-red hover:bg-[#B31721]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Swap dialog — ITEM A.3: mini-form with today's date + Yes/No selector */}
      <Dialog open={swapDialog.open} onOpenChange={(o) => setSwapDialog(p => ({ ...p, open: o }))}>
        <DialogContent data-testid="swap-dialog" className="rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{swapDialog.item} change today?</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Date</label>
              <Input data-testid="swap-date" type="date" value={todayISO()} disabled className="h-12 mt-1 bg-gray-50" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Was the {swapDialog.item} changed today?</label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <Button data-testid="swap-yes-btn"
                  onClick={() => setSwapDialog(p => ({ ...p, changed: true }))}
                  className={`h-12 rounded-xl ${swapDialog.changed === true ? "bg-brand-red text-white" : "bg-gray-100 text-gray-800"}`}>
                  Yes
                </Button>
                <Button data-testid="swap-no-btn"
                  onClick={() => setSwapDialog(p => ({ ...p, changed: false }))}
                  className={`h-12 rounded-xl ${swapDialog.changed === false ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-800"}`}>
                  No
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSwapDialog({ open: false, item: null, changed: null })}>Cancel</Button>
            <Button data-testid="submit-swap-btn" onClick={submitSwap}
              disabled={swapDialog.changed === null || swapBusy}
              className="bg-brand-red hover:bg-[#B31721]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cash Deposit dialog */}
      <Dialog open={cashOpen} onOpenChange={setCashOpen}>
        <DialogContent data-testid="cash-dialog" className="rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Cash Deposit</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Cash collected</label>
                <div className="relative">
                  <IndianRupee className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input data-testid="cash-collected-input" inputMode="numeric" className="h-14 pl-9 text-lg font-semibold"
                    value={cashCollected} onChange={(e) => setCashCollected(Number(e.target.value.replace(/\D/g, "")) || 0)} />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Cash deposited</label>
                <div className="relative">
                  <IndianRupee className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input data-testid="cash-deposited-input" inputMode="numeric" className="h-14 pl-9 text-lg font-semibold"
                    value={cashDeposited} onChange={(e) => setCashDeposited(Number(e.target.value.replace(/\D/g, "")) || 0)} />
                </div>
              </div>
            </div>
            <div className="rounded-xl bg-gray-50 p-3 text-sm">
              <div className="flex justify-between"><span className="text-gray-600">Difference</span>
                <span className={`font-semibold ${Math.abs(cashDeposited - cashCollected) > 0.5 ? "text-red-600" : "text-green-700"}`}>
                  {formatINR(cashDeposited - cashCollected)}
                </span></div>
            </div>
            <PhotoUpload value={depPhoto} onChange={setDepPhoto} testId="deposit-photo" label="Deposit slip photo" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCashOpen(false)}>Cancel</Button>
            <Button data-testid="submit-deposit-btn" onClick={submitDeposit} className="bg-brand-red hover:bg-[#B31721]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
